import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileCode,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Download,
  RefreshCw,
  ExternalLink,
  Calendar,
  User,
  Building2,
  CreditCard,
  MessageSquare,
  Link as LinkIcon
} from 'lucide-react';
import { dashboardApi } from '../../services/iso20022Api';
import { formatDate, formatCurrency } from '../../utils/helpers';
import XmlViewerModal from '../../components/iso20022/XmlViewerModal';

/**
 * RtP Transaction Detail Page
 * 
 * Displays complete details for a single pain.013 Request-to-Pay message.
 * 
 * Features:
 * - Full RtP message details (all fields)
 * - RtP lifecycle visualization (pain.013 → pacs.008 → pacs.002)
 * - Related messages via EndToEndId
 * - Party information (Initiator, Debtor, Creditor)
 * - Payment details and status
 * - XML viewer
 * - Timeline visualization
 */
const RtpTransactionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rtp, setRtp] = useState(null);
  const [relatedMessages, setRelatedMessages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [xmlModalOpen, setXmlModalOpen] = useState(false);

  useEffect(() => {
    loadRtpTransaction();
    loadRelatedMessages();
  }, [id]);

  const loadRtpTransaction = async () => {
    try {
      setLoading(true);
      const data = await dashboardApi.getRtpTransactionById(id);
      console.log('[Load RtP Detail] Transaction:', data);
      setRtp(data);
    } catch (error) {
      console.error('[Load RtP Detail] Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedMessages = async () => {
    try {
      const data = await dashboardApi.getRtpRelatedMessages(id);
      console.log('[Load Related Messages]:', data);
      setRelatedMessages(data);
    } catch (error) {
      console.error('[Load Related Messages] Error:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'APPROVED':
      case 'COMPLETED':
        return <CheckCircle2 className="w-5 h-5 text-success-600" />;
      case 'REJECTED':
      case 'FAILED':
        return <XCircle className="w-5 h-5 text-danger-600" />;
      case 'PROCESSING':
        return <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'PENDING': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'APPROVED': 'bg-success-100 text-success-800 border-success-200',
      'REJECTED': 'bg-danger-100 text-danger-800 border-danger-200',
      'PROCESSING': 'bg-blue-100 text-blue-800 border-blue-200',
      'COMPLETED': 'bg-teal-100 text-teal-800 border-teal-200',
      'FAILED': 'bg-orange-100 text-orange-800 border-orange-200'
    };

    return badges[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
          <p className="text-gray-600">Loading RtP transaction...</p>
        </div>
      </div>
    );
  }

  if (!rtp) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-900 font-medium mb-2">RtP Transaction not found</p>
          <Link to="/iso20022/rtp" className="text-primary-600 hover:text-primary-700">
            ← Back to RtP Transactions
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/iso20022/rtp')}
            className="btn-secondary flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Request-to-Pay #{rtp.id}
            </h1>
            <p className="text-gray-600 mt-1">pain.013 message details</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {rtp.rawXml && (
            <button
              onClick={() => setXmlModalOpen(true)}
              className="btn-secondary flex items-center space-x-2"
            >
              <FileCode className="w-4 h-4" />
              <span>View XML</span>
            </button>
          )}
          <button
            onClick={() => {
              loadRtpTransaction();
              loadRelatedMessages();
            }}
            className="btn-primary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`border-l-4 p-4 rounded-r-lg ${
        rtp.status === 'APPROVED' || rtp.status === 'COMPLETED' 
          ? 'bg-success-50 border-success-500'
          : rtp.status === 'REJECTED' || rtp.status === 'FAILED'
          ? 'bg-danger-50 border-danger-500'
          : rtp.status === 'PENDING'
          ? 'bg-yellow-50 border-yellow-500'
          : 'bg-blue-50 border-blue-500'
      }`}>
        <div className="flex items-start">
          <div className="flex-shrink-0 mt-0.5">
            {getStatusIcon(rtp.status)}
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium">
              RtP Status: <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(rtp.status)}`}>
                {rtp.status}
              </span>
            </h3>
            <div className="mt-2 text-sm">
              {rtp.status === 'PENDING' && (
                <p>This RtP request is awaiting approval or rejection by the debtor.</p>
              )}
              {rtp.status === 'APPROVED' && (
                <p>This RtP has been approved. Check related messages below for the pacs.008 payment.</p>
              )}
              {rtp.status === 'REJECTED' && (
                <p>This RtP request was rejected by the debtor.</p>
              )}
              {rtp.status === 'COMPLETED' && (
                <p>This RtP has been completed successfully.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Message Identifiers */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Message Identifiers</h2>
            </div>
            <div className="card-body">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Message ID</dt>
                  <dd className="mt-1 text-sm font-mono text-gray-900">{rtp.msgId}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Payment Info ID</dt>
                  <dd className="mt-1 text-sm font-mono text-gray-900">{rtp.pmtInfId}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center space-x-1">
                    <span>End-to-End ID</span>
                    <LinkIcon className="w-3 h-3 text-blue-500" title="Links to pacs.008 and pacs.002" />
                  </dt>
                  <dd className="mt-1 text-sm font-mono text-blue-600 font-semibold">{rtp.endToEndId}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Instruction ID</dt>
                  <dd className="mt-1 text-sm font-mono text-gray-900">{rtp.instrId}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Payment Information */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Payment Information</h2>
            </div>
            <div className="card-body">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Amount</dt>
                  <dd className="mt-1 text-2xl font-bold text-gray-900">{rtp.amountCcy || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Payment Method</dt>
                  <dd className="mt-1 text-sm text-gray-900">{rtp.pmtMtd || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Service Level</dt>
                  <dd className="mt-1 text-sm text-gray-900">{rtp.svcLvl || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Local Instrument</dt>
                  <dd className="mt-1 text-sm text-gray-900">{rtp.lclInstrm || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Sequence Type</dt>
                  <dd className="mt-1 text-sm text-gray-900">{rtp.seqTp || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Charge Bearer</dt>
                  <dd className="mt-1 text-sm text-gray-900">{rtp.chrgBr || 'N/A'}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500 flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>Requested Execution Date</span>
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {rtp.reqdExctnDt ? formatDate(rtp.reqdExctnDt, 'PP') : 'Not specified'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Initiating Party */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center space-x-2">
                <Building2 className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Initiating Party (RtP Creator)</h2>
              </div>
            </div>
            <div className="card-body">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="mt-1 text-sm font-semibold text-gray-900">{rtp.initgPtyName || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">ID</dt>
                  <dd className="mt-1 text-sm font-mono text-gray-900">{rtp.initgPtyId || 'N/A'}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Debtor (Payer) */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center space-x-2">
                <User className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Debtor (Payer)</h2>
              </div>
            </div>
            <div className="card-body">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="mt-1 text-sm font-semibold text-gray-900">{rtp.dbtrName || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Account ID</dt>
                  <dd className="mt-1 text-sm font-mono text-gray-900">{rtp.dbtrAcctId || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Bank/Agent Code</dt>
                  <dd className="mt-1 text-sm font-mono text-gray-900">{rtp.dbtrAgtCode || 'N/A'}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Creditor (Payee) */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center space-x-2">
                <CreditCard className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Creditor (Payee)</h2>
              </div>
            </div>
            <div className="card-body">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="mt-1 text-sm font-semibold text-gray-900">{rtp.cdtrName || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Account ID</dt>
                  <dd className="mt-1 text-sm font-mono text-gray-900">{rtp.cdtrAcctId || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Bank/Agent Code</dt>
                  <dd className="mt-1 text-sm font-mono text-gray-900">{rtp.cdtrAgtCode || 'N/A'}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Remittance Information */}
          {rtp.rmtInfUstrd && (
            <div className="card">
              <div className="card-header">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="w-5 h-5 text-gray-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Remittance Information</h2>
                </div>
              </div>
              <div className="card-body">
                <p className="text-sm text-gray-900">{rtp.rmtInfUstrd}</p>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Status Card */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Status</h2>
            </div>
            <div className="card-body space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Current Status</dt>
                <dd className="mt-1">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadge(rtp.status)}`}>
                    {getStatusIcon(rtp.status)}
                    <span className="ml-2">{rtp.status}</span>
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Direction</dt>
                <dd className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    rtp.direction === 'OUTGOING' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {rtp.direction === 'OUTGOING' ? '↑' : '↓'} {rtp.direction}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Signature</dt>
                <dd className="mt-1">
                  {rtp.signatureOk === true ? (
                    <span className="inline-flex items-center text-sm text-success-700">
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Valid
                    </span>
                  ) : rtp.signatureOk === false ? (
                    <span className="inline-flex items-center text-sm text-danger-700">
                      <XCircle className="w-4 h-4 mr-1" />
                      Invalid
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">Not signed</span>
                  )}
                </dd>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Timestamps</h2>
            </div>
            <div className="card-body space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(rtp.createdAt, 'PPpp')}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(rtp.updatedAt, 'PPpp')}</dd>
              </div>
            </div>
          </div>

          {/* Related Messages */}
          {relatedMessages && (
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">Related Messages</h2>
                <p className="text-xs text-gray-500 mt-1">Linked via EndToEndId</p>
              </div>
              <div className="card-body space-y-3">
                {relatedMessages.payment && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-green-800">pacs.008 Payment</span>
                      <span className="text-xs text-green-600">{relatedMessages.payment.status}</span>
                    </div>
                    <p className="text-sm text-gray-900 font-mono mb-2">{relatedMessages.payment.msgId}</p>
                    <p className="text-xs text-gray-600 mb-2">{relatedMessages.payment.amount}</p>
                    <Link
                      to={`/iso20022/transactions/${relatedMessages.payment.id}`}
                      className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center"
                    >
                      View Payment <ExternalLink className="w-3 h-3 ml-1" />
                    </Link>
                  </div>
                )}

                {relatedMessages.statusUpdates && relatedMessages.statusUpdates.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-700 mb-2">
                      Status Updates ({relatedMessages.statusUpdates.length})
                    </p>
                    <div className="space-y-2">
                      {relatedMessages.statusUpdates.map((status, index) => (
                        <div key={index} className="p-2 bg-gray-50 border border-gray-200 rounded text-xs">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{status.txSts}</span>
                            <span className="text-gray-500">#{status.id}</span>
                          </div>
                          <p className="text-gray-500 mt-1">{formatDate(status.createdAt, 'PP p')}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!relatedMessages.payment && (!relatedMessages.statusUpdates || relatedMessages.statusUpdates.length === 0) && (
                  <div className="text-center py-4">
                    <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No related messages yet</p>
                    {rtp.status === 'PENDING' && (
                      <p className="text-xs text-gray-400 mt-1">
                        Payment will appear after approval
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* XML Viewer Modal */}
      {rtp.rawXml && (
        <XmlViewerModal
          isOpen={xmlModalOpen}
          onClose={() => setXmlModalOpen(false)}
          xmlContent={rtp.rawXml}
          fileName={`rtp-${rtp.id}-pain013.xml`}
          title={`pain.013 RtP Message - #${rtp.id}`}
        />
      )}
    </div>
  );
};

export default RtpTransactionDetail;
